// AppContext.js
import React, {
  createContext,
  useState,
  useContext,
  useRef,
  useEffect
} from 'react';
import {
  Auth,
  HOST,
  getParam
} from "./lib/Auth";
import { useLocation } from "react-router-dom";

const AppContext = createContext();

export const useAppContext = () => useContext(AppContext);

export const AppProvider = ({ children }) => {

  const [user, setUser] = useState(() => {
    const u = localStorage.getItem("user");
    return u ? JSON.parse(u) : null;
  });
  Auth.init(user, setUser);

  const [isLoading, setIsLoading] = useState(false);
  const [menuLoaded, setMenuLoaded] = useState(false)
  const [filter, setFilter] = useState("Open");
  const [prevLocation, setPrevLocation] = useState("");
  const [chatContext, setChatContext] = useState({});
  const [chatSessionId, setChatSessionId] = useState("");
  const [locations, setLocations] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState({});
  const [staff, setStaff] = useState([]);
  const location = useLocation();
  const [threads, setThreads] = useState({});
  const [isPatient, setIsPatient] = useState(getParam('_uid') || Auth?.user?.isPatient? true : false);
  const [realtimeInitialized, setRealtimeInitialized] = useState(false);
  const realtime = useRef(null);
  // Tracks the bid-less superadmin socket created below so we neither duplicate
  // it nor leave it behind when a business gets selected.
  const superadminSocket = useRef({ active: false, workerReg: null });
  const prevLocationRef = useRef(null);
  const [serviceWorkerRegistration, setServiceWorkerRegistration] = useState(null);
  const [isAppInTestDebugMode, setIsAppInTestDebugMode] = useState(false);
  const [isProfileChanged, setIsProfileChanged] = useState(false);
  
  function sendMessageToWorker(...args) {
    if (!serviceWorkerRegistration){return}
    if (serviceWorkerRegistration.waiting) {
      serviceWorkerRegistration.waiting.postMessage(...args);
    } else if (serviceWorkerRegistration.active) {
      serviceWorkerRegistration.active.postMessage(...args);
    }
  }

  function registerServiceWorker() {
    navigator.serviceWorker.register('/service-worker.js')
      .then((registration) => {
        setServiceWorkerRegistration(registration)
      })
      .catch((error) => {
        console.log('Service Worker registration failed:', error);
      });
  }

  const unregisterAllServiceWorkers = ()=>navigator.serviceWorker.getRegistrations().then(registrations=>Promise.all(registrations.map(registration=>registration.unregister())).catch(error=>{
    console.log('Error in unregistering service workers.')
  }));
  
  
  useEffect(()=>{
    if (!('serviceWorker' in navigator)){
      return
    }
    if (getParam('_uid')){return} //if impersonating, do not unregister anything.
    unregisterAllServiceWorkers().then(()=>{
      if (!user){
        return
      }
      registerServiceWorker();
    })
  },[isPatient,user])

  useEffect(() => {
    if (!prevLocationRef.current || (location.pathname !== prevLocationRef.current)){
      setPrevLocation(prevLocationRef.current);
      prevLocationRef.current = location.pathname;
    }

  }, [location.pathname]);

  useEffect(()=>{
    setIsPatient(getParam('_uid') || Auth?.user?.isPatient? true : false)
  },[location, Auth.user])
  
  useEffect(()=>{
    setMenuLoaded(false)
  },[selectedBusiness])

  useEffect(() => {
    // A superadmin working outside any business (the bid-less dashboard) matches
    // none of the branches below, so without this they get no socket at all —
    // and therefore no support notifications and no menu badge updates.
    const wantsSuperadminSocket = !!(
      user
      // Platform superadmins only. `isSuperAdmin` alone means superadmin-OR-partner-
      // admin, and a bid-less PARTNER admin would open a uid-keyed socket it can never
      // receive anything on — superadmin topics refuse partner admins server-side.
      // Same expression the support widget gates on: the precise flag from login,
      // with the inference as fallback for a login shape cached from before the flag.
      && (user.isPlatformSuperadmin ?? (user.isSuperAdmin && !user.isPartnerAdmin))
      && !isPatient
      && !getParam('_uid')
      && !selectedBusiness?.bid
    );

    if (superadminSocket.current.active && !wantsSuperadminSocket) {
      // Leaving the bid-less superadmin state: drop our socket so the branches
      // below can build the right one (or none at all).
      sendMessageToWorker({ command: 'disconnect' });
      if (realtime.current) {
        realtime.current.disconnect();
      }
      realtime.current = null;
      superadminSocket.current = { active: false, workerReg: null };
      setRealtimeInitialized(false);
    }

    if (user && isPatient && !realtime.current) {
      const params = {
        reconnectionAttempts: 20,
        reconnectionDelayMax: 60000,
        auth: {
          Authorization: `Bearer ${user.token}`,
        },
        transports: ['websocket'],
        cdnBaseURL: './',
        query: {
          ...(getParam('_uid') ? { _uid: getParam('_uid') } : {}),
        },
      }
      if (!getParam('_uid')){
        //only initialize if it's a real patient (not impersonation)
        sendMessageToWorker({ 
          command : 'initialize',
          host : `${HOST}/`,
          params
        });
      }
      realtime.current = new window.Realtime.Realtime(`${HOST}/`, params);
      setRealtimeInitialized(true);
      realtime.current.connect();
      return;
    }

    if (wantsSuperadminSocket) {
      const params = {
        reconnectionAttempts: 20,
        reconnectionDelayMax: 60000,
        auth: {
          Authorization: `Bearer ${user.token}`,
        },
        transports: ['websocket'],
        cdnBaseURL: './',
        query: {},
      }
      if (realtime.current && !superadminSocket.current.active) {
        //a business scoped socket is still up (the business was just cleared)
        sendMessageToWorker({ command : 'disconnect' });
        realtime.current.disconnect();
        realtime.current = null;
      }
      if (!realtime.current) {
        realtime.current = new window.Realtime.Realtime(`${HOST}/`, params);
        superadminSocket.current.active = true;
        setRealtimeInitialized(true);
        realtime.current.connect();
      }
      if (superadminSocket.current.workerReg !== serviceWorkerRegistration) {
        //the service worker registers after the first render, so (re)initialize
        //it whenever a new registration shows up
        sendMessageToWorker({ command : 'disconnect' });
        sendMessageToWorker({
          command : 'initialize',
          host : `${HOST}/`,
          params
        });
        superadminSocket.current.workerReg = serviceWorkerRegistration;
      }
      return;
    }

    if (user && selectedBusiness && selectedBusiness.bid) {
      if (!selectedBusiness || !selectedBusiness.bid && realtime.current) {
        realtime.current.setCurrentBusinessIds([]);
      } else if (selectedBusiness && selectedBusiness.bid) {
        if (realtime.current) {
          sendMessageToWorker({ 
            command : 'disconnect'
          });
          realtime.current.disconnect();
        }
        const params = {
          reconnectionAttempts: 20,
          reconnectionDelayMax: 60000,
          auth: {
            Authorization: `Bearer ${user.token}`,
          },
          transports: ['websocket'],
          cdnBaseURL: './',
          query: {
            bid: selectedBusiness?.bid,
            visitor_id: '',
          },
        }
        sendMessageToWorker({ 
          command : 'initialize',
          host : `${HOST}/`,
          params
        });
        sendMessageToWorker({ 
            command : 'setCurrentBusinessIds',
            params : [selectedBusiness.bid]
        });
        realtime.current = new window.Realtime.Realtime(`${HOST}/`, params);
        setRealtimeInitialized(true);
        realtime.current.setCurrentBusinessIds([selectedBusiness.bid]);
        realtime.current.connect();
      }
    }

    // Tear down realtime on logout so auto-reconnect doesn't bring it back.
    if (!user && realtime.current) {
      sendMessageToWorker({ command: 'disconnect' });
      realtime.current.disconnect();
      realtime.current = null;
      setRealtimeInitialized(false);
    }
  }, [user, selectedBusiness, isPatient, serviceWorkerRegistration, realtime]); // Ensure the dependency array is correct
  

  const value = {
    user,
    setUser,
    isLoading,
    setIsLoading,
    chatContext,
    setChatContext,
    chatSessionId,
    setChatSessionId,
    locations,
    setLocations,
    selectedBusiness,
    setSelectedBusiness,
    threads,
    setThreads,
    location,
    staff,
    setStaff,
    realtime,
    realtimeInitialized,
    prevLocation,
    filter,
    setFilter,
    isPatient,
    serviceWorkerRegistration,
    sendMessageToWorker,
    isAppInTestDebugMode,
    setIsAppInTestDebugMode,
    isProfileChanged,
    setIsProfileChanged,
    menuLoaded,
    setMenuLoaded
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
